import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { ChartBar, Money, ArrowUpRight } from "@phosphor-icons/react";

const PERIODS = [
  { value: "day", label: "Daily", days: 30 },
  { value: "week", label: "Weekly", days: 84 },
  { value: "month", label: "Monthly", days: 365 },
];

export default function PartnerReportsPage() {
  const [period, setPeriod] = useState("day");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const p = PERIODS.find((x) => x.value === period);
    api.get("/partner/reports/income", { params: { period, days: p.days } })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [period]);

  // Simple bar chart — pure CSS, no libs
  const maxGross = data ? Math.max(...data.rows.map((r) => r.gross), 1) : 1;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="partner-reports-page">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Hotel Partner</p>
      <h1 className="font-serif text-4xl lg:text-5xl text-midnight mb-8 flex items-center gap-3">
        <ChartBar size={36} weight="duotone" className="text-gold" /> Income Reports
      </h1>

      <div className="flex gap-2 mb-8 flex-wrap">
        {PERIODS.map((p) => (
          <button key={p.value} onClick={() => setPeriod(p.value)} data-testid={`period-${p.value}`}
            className={`px-5 py-2 rounded-full text-xs tracking-wide border ${
              period === p.value ? "bg-midnight text-bone border-midnight" : "bg-white text-neutral-600 border-black/10"
            }`}>{p.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="h-96 bg-stone rounded-2xl animate-pulse" />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <StatCard label="Gross Revenue" value={data.total_gross} highlight />
            <StatCard label="Net Earnings (90%)" value={data.total_net} accent />
            <StatCard label="Total Bookings" value={data.total_bookings} suffix=" " isCount />
          </div>

          <div className="bg-white border border-black/5 rounded-2xl p-6" data-testid="report-chart">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-xl text-midnight">Revenue Trend</h3>
              <p className="text-xs text-neutral-500">Last {data.days} days · {data.rows.length} periods</p>
            </div>

            {data.rows.length === 0 ? (
              <div className="py-16 text-center">
                <p className="text-neutral-500">No paid bookings in this window yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {data.rows.map((r) => (
                  <div key={r.period} className="flex items-center gap-4" data-testid={`row-${r.period}`}>
                    <div className="w-20 text-xs text-neutral-500 shrink-0">{r.period}</div>
                    <div className="flex-1 relative h-8 bg-stone rounded-lg overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-gold to-midnight rounded-lg transition-all"
                           style={{ width: `${(r.gross / maxGross) * 100}%` }} />
                      <div className="absolute inset-0 flex items-center justify-between px-3">
                        <span className="text-xs text-midnight font-semibold">₹{r.gross.toLocaleString("en-IN")}</span>
                        <span className="text-[10px] text-midnight/70">{r.bookings} bkg</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, highlight, accent, suffix = "₹ ", isCount }) {
  return (
    <div className={`p-6 rounded-2xl border ${highlight ? "bg-gold/10 border-gold/30" : accent ? "bg-emerald-50 border-emerald-200" : "bg-white border-black/5"}`}>
      <p className="text-[10px] tracking-widest uppercase text-neutral-500 mb-2">{label}</p>
      <p className="font-serif text-3xl text-midnight flex items-baseline gap-1">
        {isCount ? value : <>{suffix.trim()} {value.toLocaleString("en-IN")}</>}
        <ArrowUpRight size={16} className="text-emerald-600" />
      </p>
    </div>
  );
}
